/**
 * Curve smoothing utilities using Catmull-Rom splines
 */

import type { ControlPoint, Coordinate, Drawing, PathSegment } from '@/types'

/**
 * Extract main path coordinates from a drawing in segment order.
 * Maintains proper segment traversal order instead of using Object.values().
 * Filters out bezier control points, returning just the path through-points.
 *
 * @param drawing - The drawing to extract coordinates from
 * @returns Array of coordinates in segment order
 */
export function extractMainCoordinates(drawing: Drawing): Coordinate[] {
	if (drawing.segments.length === 0) return []

	const visited = new Set<string>()

	return drawing.segments.flatMap(segment =>
		segment.pointIds
			.map(pointId => {
				const point = drawing.points[pointId]
				if (!point || visited.has(pointId)) return null
				visited.add(pointId)
				return { x: point.x, y: point.y }
			})
			.filter((coord): coord is Coordinate => coord !== null)
	)
}

/**
 * Catmull-Rom to Bezier conversion.
 * Given 4 points (p0, p1, p2, p3), computes cubic bezier control points
 * for the segment from p1 to p2.
 *
 * @param p0 - First control point
 * @param p1 - Start point of the segment
 * @param p2 - End point of the segment
 * @param p3 - Last control point
 * @param tension - Tension parameter (default: 0.5)
 * @returns Bezier control points with start, cp1, cp2, and end
 */
export function catmullRomToBezier(
	p0: Coordinate,
	p1: Coordinate,
	p2: Coordinate,
	p3: Coordinate,
	tension: number = 0.5,
): { start: Coordinate; cp1: Coordinate; cp2: Coordinate; end: Coordinate } {
	const t = tension

	// Catmull-Rom to Bezier conversion formulas
	const cp1: Coordinate = {
		x: p1.x + ((p2.x - p0.x) / 6) * t,
		y: p1.y + ((p2.y - p0.y) / 6) * t,
	}

	const cp2: Coordinate = {
		x: p2.x - ((p3.x - p1.x) / 6) * t,
		y: p2.y - ((p3.y - p1.y) / 6) * t,
	}

	return { start: p1, cp1, cp2, end: p2 }
}


/**
 * Get the start point coordinate from a segment.
 */
function getSegmentStartPoint(
	segment: PathSegment,
	points: Record<string, ControlPoint>,
): Coordinate | null {
	const firstPointId = segment.pointIds[0]
	if (!firstPointId) return null
	const point = points[firstPointId]
	return point ? { x: point.x, y: point.y } : null
}

/**
 * Get the end point coordinate from a segment.
 */
function getSegmentEndPoint(
	segment: PathSegment,
	points: Record<string, ControlPoint>,
): Coordinate | null {
	const lastPointId = segment.pointIds[segment.pointIds.length - 1]
	if (!lastPointId) return null
	const point = points[lastPointId]
	return point ? { x: point.x, y: point.y } : null
}

/**
 * Extract coordinates from all segments in order.
 */
function extractSegmentCoordinates(
	drawing: { points: Record<string, ControlPoint>; segments: PathSegment[] },
): Coordinate[] {
	return drawing.segments.flatMap((segment, i) => {
		const coords: Coordinate[] = []

		if (i === 0) {
			const start = getSegmentStartPoint(segment, drawing.points)
			if (start) coords.push(start)
		}

		const end = getSegmentEndPoint(segment, drawing.points)
		if (end) coords.push(end)

		return coords
	})
}

/**
 * Build a points record from coordinates.
 */
function buildPointsRecord(coords: Coordinate[]): Record<string, ControlPoint> {
	return coords.reduce((acc, coord, i) => {
		const id = `p-${i}`
		acc[id] = {
			id,
			x: coord.x,
			y: coord.y,
			type: i === 0 ? 'start' : i === coords.length - 1 ? 'end' : 'corner',
		}
		return acc
	}, {} as Record<string, ControlPoint>)
}

/**
 * Build line segments connecting consecutive points.
 */
function buildLineSegments(pointCount: number): PathSegment[] {
	return Array.from({ length: pointCount - 1 }, (_, i) => ({
		type: 'line' as const,
		pointIds: [`p-${i}`, `p-${i + 1}`],
	}))
}

/**
 * Convert a curved drawing back to sharp (line segments).
 * Handles NEW format only (pointIds.length === 2 for cubic curves).
 * Extracts coordinates from segments and rebuilds as a simple line-based path.
 *
 * @param drawing - The drawing with curved segments to convert
 * @returns A new drawing with line segments only
 */
export function convertToSharp(
	drawing: { points: Record<string, ControlPoint>; segments: PathSegment[] },
): { points: Record<string, ControlPoint>; segments: PathSegment[] } {
	const coords = extractSegmentCoordinates(drawing)
	const points = buildPointsRecord(coords)
	const segments = buildLineSegments(coords.length)
	return { points, segments }
}
